import { ConversationTokenPayload } from "../utils/jwtUtils";
import { store } from "../store/store";
import { selectDemoMode } from "../store/flagsSlice";

// Server URL constants using domain names
export const SERVER_URLS = {
  CLOUD: "staging.persona-ai.ai",
  STAGE: "staging.persona-ai.ai",
  STAGING: "staging.persona-ai.ai",
  PROD: "apps.persona-ai.ai",
  PRODUCTION: "apps.persona-ai.ai",
  QA: "qa.persona-ai.ai",
  DEMO: "demo.persona-ai.ai",
  // Devboxes at public, caddy https-secured domains.
  ANDY2: "andy.devboxes.personaps.com",
  ANDY: "andy.devboxes.personaps.com",
  AGAVIN: "andy.devboxes.personaps.com",
  CESAR: "cesar.devboxes.personaps.com",
  SCOTT: "scott.devboxes.personaps.com",
  COLIN: "colin.devboxes.personaps.com",
  ELI: "eli.devboxes.personaps.com",
  COLM: "colm.devboxes.personaps.com",
  FRANK: "frank.devboxes.personaps.com",
  ADVAITH: "advaith.devboxes.personaps.com",
  SHARANG: "sharang.devboxes.personaps.com",
  JAKE: "jake.devboxes.personaps.com",
  LOCAL: "localhost",
};

export const isInsecure = (environment: string): boolean => {
  return environment.toUpperCase() === "DEV";
}

export const isSecure = (environment: string): boolean => {
  return !isInsecure(environment);
}

const getEnvOverride = () => new URLSearchParams(window.location.search).get("envOverride") ?? import.meta.env.VITE_ENV_OVERRIDE;

export const getEffectiveEnvironment = (env: string, preventOverride?: boolean) => (preventOverride && env.trim() !== "") ? env : (getEnvOverride() || env);

export const getLocalEnvOverride = () => import.meta.env.VITE_LOCAL_ENV_OVERRIDE;

export const getForcePortOverride = (): number | undefined => {
  const val = import.meta.env.VITE_FORCE_PORT_OVERRIDE;
  const parsed = val !== undefined ? parseInt(val, 10) : NaN;
  return isNaN(parsed) ? undefined : parsed;
};

export const getForceMediaPortOverride = (): number | undefined => {
  const val = import.meta.env.VITE_FORCE_MEDIA_PORT_OVERRIDE;
  const parsed = val !== undefined ? parseInt(val, 10) : NaN;
  return isNaN(parsed) ? undefined : parsed;
};

export const getEffectiveBaseUrl = (): string => {
  const env = getEffectiveEnvironment("PRODUCTION");
  const local = getLocalEnvOverride() ?? undefined;
  const forcePort = getForcePortOverride() ?? undefined;
  const baseHttpUrl = getHttpUrl(env, local, forcePort);
  return baseHttpUrl;
};

export const getEffectiveBaseMediaUrl = (): string => {
  const env = getEffectiveEnvironment("PRODUCTION");
  const local = getLocalEnvOverride() ?? undefined;
  const forcePort = getForceMediaPortOverride() ?? undefined;
  const baseHttpUrl = getHttpUrl(env, local, forcePort);
  return baseHttpUrl;
};

interface BaseDomain {
  domain: string;
  is_insecure: boolean;
}

const getDomainAndSecurity = (env: string, local?: string, preventOverride?: boolean): BaseDomain => {
  // Check if demo mode is active
  const isDemoMode = selectDemoMode(store.getState());
  if (isDemoMode) {
    return { domain: SERVER_URLS.DEMO, is_insecure: false };
  }

  const key = getEffectiveEnvironment(env, preventOverride).toUpperCase();
  const domain =
    key === "DEV"
      ? local
        ? SERVER_URLS[local.toUpperCase() as keyof typeof SERVER_URLS]
        : "localhost"
      : SERVER_URLS[key as keyof typeof SERVER_URLS];
  if (!domain) throw new Error(`Unknown environment key: ${key}`);
  const insecure = isInsecure(key);
  //console.log("getBaseDomain ", env, local, "=>", domain, insecure );
  return { domain, is_insecure: insecure };
};

export const getWebSocketUrl = (conv: string, env: string, local?: string, preventOverride?: boolean): string => {
  const { domain, is_insecure } = getDomainAndSecurity(env, local, preventOverride);
  const protocol = is_insecure ? "ws" : "wss";
  const port = is_insecure ? ":8001" : "";
  return `${protocol}://${domain}${port}/v1/puppet/psaudio/ws/${conv}`;
};

export const getHttpUrl = (env: string, local?: string, forcePort?: number, preventOverride?: boolean): string => {
  const { domain, is_insecure } = getDomainAndSecurity(env, local, preventOverride);
  const protocol = is_insecure ? "http" : "https";
  const port = 
    forcePort === 0 ? "" : 
    forcePort !== undefined ? `:${forcePort}` : 
    is_insecure ? ":8001" : "";
  return `${protocol}://${domain}${port}/`;
};

export const getWebSocketUrlFromToken = (t: ConversationTokenPayload): string => {
  return getWebSocketUrl(
    t.conversation_id,
    t.environment,
    t.local_dev_connection_method
  );
};

export const getBaseHTTPUrlFromToken = (t: ConversationTokenPayload): string => {
  return getHttpUrl(
    t.environment,
    t.local_dev_connection_method
  );
};
